import { PayloadAction, createSlice } from '@reduxjs/toolkit';

export interface SignUp {
  loading: boolean;
  error: string | null;
  success: boolean | null;
  data: SignUpData;
  message: string | null;
  updateAgencySuccess?: boolean | null;
}

export interface SignUpData {
  phoneNumber: string | null;
  isOTPsent?: boolean | null;
  isNumberAlreadyRegistered?: boolean | null;
  isOTPValid?: boolean | null;
  companyDetails?: CompanyDetails | null;
  stage?: number | null;
  isSignInOTPsent?: boolean | null;
  isSignUpNeeded?: boolean | null;
  message?: string;
}

export interface CompanyDetails {
  id?: string | null;
  name?: string | null;
  companyName?: string | null;
  designation?: string | null;
  otherDesignation?: string | null;
  emailId?: string | null;
  pincode?: string | null;
  address?: string | null;
  area?: string | null;
  city?: string | null;
  state?: string | null;
}

export const signUpDetailsState: SignUp = {
  data: {
    phoneNumber: null,
    isOTPsent: false,
    isNumberAlreadyRegistered: false,
    isOTPValid: false,
    companyDetails: {
      id: '',
      name: '',
      companyName: '',
      designation: '',
      otherDesignation: '',
      emailId: '',
      pincode: '',
      address: '',
      area: '',
      city: '',
      state: '',
    },
    stage: 0,
    isSignInOTPsent: null,
    isSignUpNeeded: null,
  },
  loading: false,
  error: null,
  success: null,
  message: '',
  updateAgencySuccess: null,
};

interface SendOTP {
  phoneNumber: string;
}

interface VerifyOTP {
  phoneNumber: string;
  otp: string;
}

const SignUpSlice = createSlice({
  name: 'form',
  initialState: signUpDetailsState,
  reducers: {
    sendOTP: (state, action: PayloadAction<SendOTP>) => {
      state.loading = true;
      state.error = null;
      state.data.isOTPsent = false;
    },
    sendSignInOTP: (state, action: PayloadAction<SendOTP>) => {
      state.loading = true;
      state.error = null;
      state.data.isSignInOTPsent = null;
    },
    verifyOTP: (state, action: PayloadAction<VerifyOTP>) => {
      state.loading = true;
      state.error = null;
      state.data.isOTPsent = false;
    },
    registerCompany: (state, action: PayloadAction<CompanyDetails>) => {
      state.loading = true;
      state.error = null;
    },
    updateTempCompany: (state, action: PayloadAction<CompanyDetails>) => {
      state.loading = true;
      state.error = null;
    },
    setSendOTP: (state, action: PayloadAction<SignUpData>) => {
      state.data.phoneNumber = action.payload.phoneNumber;
      state.data.isOTPsent = true;
      state.data.isNumberAlreadyRegistered = false;
      state.loading = false;
      // If isSignUpNeeded is provided in the payload, update it
      if (action.payload.isSignUpNeeded !== undefined) {
        state.data.isSignUpNeeded = action.payload.isSignUpNeeded;
      }
      state.message = action.payload.message ?? '';
    },
    setSignInSendOTPSuccess: (state, action: PayloadAction<SignUpData>) => {
      state.data.phoneNumber = action.payload.phoneNumber;
      state.data.isSignInOTPsent = true;
      state.data.isNumberAlreadyRegistered = true;
    },
    setSignInSendOTPFailure: (state, action: PayloadAction<SignUpData>) => {
      state.data.phoneNumber = action.payload.phoneNumber;
      state.data.isSignInOTPsent = false;
      state.data.isNumberAlreadyRegistered = false;
    },
    setValidOTP: (state, action: PayloadAction<CompanyDetails | null>) => {
      state.data.isOTPValid = true;
      state.data.isOTPsent = false;
      state.data.isSignInOTPsent = null;
      if (state.data.companyDetails) {
        state.data.companyDetails = action?.payload;
      }
    },
    setCompanyDetails: (state, action: PayloadAction<CompanyDetails>) => {
      state.data.companyDetails = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.success = false;
    },
    setSuccess: (state, action: PayloadAction<boolean | null>) => {
      state.success = action.payload;
    },
    setStage: (state, action: PayloadAction<number | null>) => {
      state.data.stage = action.payload;
    },
    setDefaultSignIn: (state, action: PayloadAction<any>) => {
      state.data.isSignInOTPsent = null;
      state.data.isNumberAlreadyRegistered = false;
    },
    setUpdateAgencySuccess: (state, action: PayloadAction<boolean | null>) => {
      state.updateAgencySuccess = action.payload;
    },
  },
});

export const {
  setSendOTP,
  setLoading,
  setError,
  setValidOTP,
  setCompanyDetails,
  sendOTP,
  verifyOTP,
  registerCompany,
  updateTempCompany,
  setStage,
  setSuccess,
  sendSignInOTP,
  setSignInSendOTPSuccess,
  setSignInSendOTPFailure,
  setDefaultSignIn,
  setUpdateAgencySuccess,
} = SignUpSlice.actions;

export default SignUpSlice.reducer;
